import { useCallback, useEffect, useState } from "react";
import {
  Container,
  Sidebar,
  TemplateContainer,
  TemplateGrid,
  CategoryList,
  Header,
  TemplatesGrid,
  UploadButtonStyles,
  ErrorPageContainer,
  CategoryHeader,
  AddCategoryButton,
} from "../StyledComponents";
import {
  AddCategoryIcon,
  ErrorPageIcon,
  NoTemplatesIcon,
  UploadIcon,
} from "../../../assets/icons";
import { Button } from "@mui/material";
import ErrorToast from "../../../components/Notification/ErrorToast";
import PrintPreview from "../../../components/PrintPreview/PrintPreview";
import Loader from "../../../components/Loader/Loader";
import TemplateCardItem from "../../../components/TemplateCard/TemplateCardItem";
import {
  addNewFirm,
  updateFirm,
  deleteFirm,
  fetchLetterHeadTemplates,
  fetchFirms,
  uploadLetterheadTemplate,
  updateLetterheadTemplate,
  deleteLetterheadTemplate,
  fetchLetterheadTemplatePreview,
} from "../../../service/settings";
import AddCategoryModal from "../../../components/AddCategoryModal/AddCategoryModal";
import UploadLetterheadModal from "../../../components/UploadLetterheadModal/UploadLetterheadModal";
import CustomModal from "../../../components/common/CustomModal/CustomModal";
import { Dropdown, Tooltip } from "antd";
import {
  handleCreateCategoryUtil,
  handleDeleteFirmUtil,
  handlePreviewUtil,
  handleUpdateFirmUtil,
  fetchLetterheadThumbnail,
} from "./LetterHeadUtils";
import EmptyState from "../../../components/EmptyState";

const LetterHead = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [templatesByCategory, setTemplatesByCategory] = useState({});

  const [templatePreview, setTemplatePreview] = useState({
    file_content: "",
    file_type: "",
    content_type: "",
    filename: "",
    file_url: "",
  });

  const [uploading, setUploading] = useState(false);
  const [toastMessages, setToastMessages] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [firmModalMode, setFirmModalMode] = useState("add");
  const [uploadModal, setUploadModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [templateToEditWithFilenames, setTemplateToEditWithFilenames] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteFirmModal, setDeleteFirmModal] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [error, setError] = useState("");


  const templates = templatesByCategory[selectedCategory] || [];
  const selectedFirm =
    categories.find((category) => category.key === selectedCategory) || null;
  const canDeleteSelectedFirm =
    selectedFirm && Number(selectedFirm.count) === 0;

  const handleCategoryClick = async (key) => {
    setSelectedCategory(key);

    if (!templatesByCategory[key]) setisLoading(true);

    try {
      const res = await fetchLetterHeadTemplates(key);
      // Response is an array of templates
      const templates = res.data.data || [];

      setTemplatesByCategory((prev) => ({
        ...prev,
        [key]: templates,
      }));
      
      // Refresh firms list to get updated letterhead counts
      const firmsRes = await fetchFirms();
      const firms = firmsRes.data.data || [];
      const mappedCategories = firms.map((firm) => ({
        id: firm.id,
        key: firm.id.toString(),
        label: firm.name,
        count: firm.letterhead_count || 0,
        is_active: firm.is_active,
      }));
      setCategories(mappedCategories);
    } catch (error) {
      console.error(`Failed to load templates for ${key}`, error);
      handleToastMessage(`Failed to load templates.`);
    } finally {
      setisLoading(false);
    }
  };


  const handleToastMessage = (message, success = false) => {
    const id = Date.now().toString();

    const newToast = {
      id,
      newMsg: message,
      success,
    };

    setToastMessages([newToast]);
    setShowToast(true);
  };

  const handleCloseCategoryModal = () => {
    setAddCategoryModal(false);
    setFirmModalMode("add");
    setError("");
    setCategoryName("");
  };

  const handleOpenAddFirmModal = () => {
    setFirmModalMode("add");
    setCategoryName("");
    setError("");
    setAddCategoryModal(true);
  };

  const handleOpenEditFirmModal = () => {
    if (!selectedFirm) {
      handleToastMessage("Select a firm before editing.");
      return;
    }
    setFirmModalMode("edit");
    setCategoryName(selectedFirm.label || "");
    setError("");
    setAddCategoryModal(true);
  };

  const handleFirmActionClick = ({ key }) => {
    if (key === "add") {
      handleOpenAddFirmModal();
      return;
    }
    if (key === "edit") {
      handleOpenEditFirmModal();
      return;
    }
    if (key === "delete" && canDeleteSelectedFirm) {
      setDeleteFirmModal(selectedFirm);
    }
  };

  const firmActionItems = [
    { key: "add", label: "Add" },
    { key: "edit", label: "Edit", disabled: !selectedFirm },
    {
      key: "delete",
      label: canDeleteSelectedFirm ? "Delete" : "Delete (empty only)",
      disabled: !canDeleteSelectedFirm,
      danger: true,
    },
  ];

  const handleUploadLetterhead = async (uploadData) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("firm", uploadData.firm);
      formData.append("firm_location", uploadData.firm_location);
      formData.append("header_image", uploadData.header_file);
      formData.append("footer_image", uploadData.footer_file);
      if (uploadData.is_default) {
        formData.append("is_default", uploadData.is_default);
      }

      // Call the API to upload letterhead template (header/footer images for DOCX merging)
      await uploadLetterheadTemplate(formData);

      // Refresh the templates list after successful upload
      await handleCategoryClick(selectedCategory);

      setUploadModal(false);
      handleToastMessage("Letterhead uploaded successfully", true);
    } catch (err) {
      console.error("Upload error:", err);
      handleToastMessage(
        err?.response?.data?.error || err?.response?.data?.detail || err?.response?.data?.message || "File upload failed. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleEditLetterhead = (template) => {
    setTemplateToEdit(template);
    setTemplateToEditWithFilenames(null);
    setEditModal(true);
  };

  // Fetch letterhead details when edit modal opens, to get header/footer filenames if list API doesn't include them
  useEffect(() => {
    if (!editModal || !templateToEdit?.id) return;
    const hasFilenames = templateToEdit?.header_filename || templateToEdit?.footer_filename ||
      templateToEdit?.header_image?.filename || templateToEdit?.footer_image?.filename ||
      templateToEdit?.file_filename;
    if (hasFilenames) {
      setTemplateToEditWithFilenames(templateToEdit);
      return;
    }
    let cancelled = false;
    fetchLetterheadTemplatePreview(templateToEdit.id)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || res?.data;
        const headerFilename = data?.header_image?.filename || data?.header_image?.name;
        const footerFilename = data?.footer_image?.filename || data?.footer_image?.name;
        if (headerFilename || footerFilename) {
          setTemplateToEditWithFilenames({
            ...templateToEdit,
            header_filename: headerFilename || templateToEdit.header_filename,
            footer_filename: footerFilename || templateToEdit.footer_filename,
          });
        } else {
          setTemplateToEditWithFilenames(templateToEdit);
        }
      })
      .catch(() => setTemplateToEditWithFilenames(templateToEdit));
    return () => { cancelled = true; };
  }, [editModal, templateToEdit]);

  const handleUpdateLetterhead = async (uploadData) => {
    try {
      setUploading(true);

      const formData = new FormData();
      if (uploadData.firm_location) {
        formData.append("firm_location", uploadData.firm_location);
      }
      if (uploadData.header_file) {
        formData.append("header_image", uploadData.header_file);
      }
      if (uploadData.footer_file) {
        formData.append("footer_image", uploadData.footer_file);
      }
      if (uploadData.remove_header_image) {
        formData.append("remove_header_image", "true");
      }
      if (uploadData.remove_footer_image) {
        formData.append("remove_footer_image", "true");
      }
      if (uploadData.is_default !== undefined) {
        formData.append("is_default", uploadData.is_default);
      }

      // Call the API to update letterhead template
      await updateLetterheadTemplate(templateToEdit.id, formData);

      // Refresh the templates list after successful update
      await handleCategoryClick(selectedCategory);

      setEditModal(false);
      setTemplateToEdit(null);
      handleToastMessage("Letterhead updated successfully", true);
    } catch (err) {
      console.error("Update error:", err);
      handleToastMessage(
        err?.response?.data?.error || err?.response?.data?.detail || err?.response?.data?.message || "Failed to update letterhead. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLetterhead = (template) => {
    setTemplateToDelete(template);
    setDeleteModal(true);
  };

  const confirmDeleteLetterhead = async () => {
    if (!templateToDelete) return;

    try {
      setisLoading(true);
      await deleteLetterheadTemplate(templateToDelete.id);
      await handleCategoryClick(selectedCategory);
      setDeleteModal(false);
      setTemplateToDelete(null);
      handleToastMessage("Letterhead deleted successfully", true);
    } catch (err) {
      console.error("Delete error:", err);
      handleToastMessage(
        err?.response?.data?.error || err?.response?.data?.detail || err?.response?.data?.message || "Failed to delete letterhead. Please try again."
      );
    } finally {
      setisLoading(false);
    }
  };

  const getTemplateHandlers = (template) => ({
    onPreview: () =>
      handlePreviewUtil({
        template,
        setIsLoading: setisLoading,
        setTemplatePreview,
        setOpenPreview,
        handleToastMessage,
      }),
    onEdit: () => handleEditLetterhead(template),
    onDelete: () => handleDeleteLetterhead(template),
  });

  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownId !== null) {
        setOpenDropdownId(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openDropdownId]);


  const loadInitialTemplates = useCallback(
    async (selectedKeyOverride) => {
      setisLoading(true);
      try {
        // Fetch firms list first
        const firmsRes = await fetchFirms();
        const firms = firmsRes.data.data || [];
        
        // Map firms to categories format
        const mappedCategories = firms.map((firm) => ({
          id: firm.id,
          key: firm.id.toString(),
          label: firm.name,
          count: firm.letterhead_count || 0,
          is_active: firm.is_active,
        }));

        setCategories(mappedCategories);

        const firmKeys = new Set(mappedCategories.map((firm) => firm.key));
        const preferredKey =
          selectedKeyOverride !== undefined ? selectedKeyOverride : selectedCategory;
        const defaultKey = firmKeys.has(preferredKey)
          ? preferredKey
          : mappedCategories?.[0]?.key || "";
        setSelectedCategory(defaultKey);

        // Fetch templates for the selected firm
        if (defaultKey) {
          const res = await fetchLetterHeadTemplates(defaultKey);
          // Response is an array of templates
          const templates = res.data.data || [];

          setTemplatesByCategory({
            [defaultKey]: templates,
          });
        }
      } catch (error) {
        setShowErrorPage(true);
        console.error("Failed to load templates", error);
      } finally {
        setisLoading(false);
      }
    },
    [selectedCategory]
  );

  useEffect(() => {
    setisLoading(true);
    loadInitialTemplates();
  }, []);

  const handleSaveFirm = () => {
    if (firmModalMode === "edit") {
      return handleUpdateFirmUtil({
        firmName: categoryName,
        selectedFirm,
        updateFirm,
        loadInitialTemplates,
        handleToastMessage,
        setAddCategoryModal,
        setIsLoading: setisLoading,
        setCategoryName,
        setError,
      });
    }

    return handleCreateCategoryUtil({
      categoryName,
      addNewFirm,
      loadInitialTemplates,
      handleToastMessage,
      setAddCategoryModal,
      setIsLoading: setisLoading,
      setCategoryName,
      setError,
      setCategories,
      setSelectedCategory,
    });
  };

  return showErrorPage ? (
    <ErrorPageContainer>
      <div>
        <ErrorPageIcon />
      </div>
      <div>
        Unable to load data. Please refresh the page or try again later.
      </div>
    </ErrorPageContainer>
  ) : (
    <Container>
      <Sidebar>
        <Header>
          <CategoryHeader>Firms</CategoryHeader>
          <Dropdown
            menu={{ items: firmActionItems, onClick: handleFirmActionClick }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <AddCategoryButton
              data-testid="add-category-btn"
              onClick={(event) => event.preventDefault()}
            >
              <AddCategoryIcon />
            </AddCategoryButton>
          </Dropdown>
        </Header>

        <CategoryList>
          {categories?.map((cat) => (
            <button
              key={cat.key}
              id={cat.key}
              onClick={() => handleCategoryClick(cat.key)}
              className={selectedCategory === cat.key ? "active" : ""}
            >
              <Tooltip
                placement="right"
                title={cat.label}
                align={{
                  offset: [10, 0],
                }}
              >
                <span className="label">{cat.label}</span>
              </Tooltip>
            </button>
          ))}
        </CategoryList>
      </Sidebar>

      <TemplateContainer>
        <Header>
          <div>
            {categories.find((c) => c.key === selectedCategory)?.label+" - Letterheads" || ""}
          </div>
          <Button
            variant="contained"
            endIcon={<UploadIcon />}
            disabled={uploading || !selectedCategory}
            onClick={(e) => {
              e.stopPropagation();
              setUploadModal(true);
            }}
            sx={UploadButtonStyles}
          >
            Upload
          </Button>
        </Header>
        <TemplatesGrid>
          <div className="scroll-wrapper">
            {templates?.length === 0 && (
              <EmptyState
                icon={NoTemplatesIcon}
                message="No Letterheads Added"
              />
            )}

            {templates.length > 0 && (
              <TemplateGrid>
                {templates.map((template) => (
                  <TemplateCardItem
                    key={template.id}
                    isEnabled={template?.is_active}
                    template={template}
                    openDropdownId={openDropdownId}
                    setOpenDropdownId={setOpenDropdownId}
                    fetchThumbnail={fetchLetterheadThumbnail}
                    mode="letterhead"
                    {...getTemplateHandlers(template)}
                  />
                ))}
              </TemplateGrid>
            )}
          </div>
        </TemplatesGrid>
      </TemplateContainer>
      {openPreview && (
        <PrintPreview
          printData={templatePreview}
          setOpenPreview={setOpenPreview}
        />
      )}
      {addCategoryModal && (
        <AddCategoryModal
          isOpen={addCategoryModal}
          categoryName={categoryName}
          header={firmModalMode === "edit" ? "Edit Firm" : "Create new Firm"}
          placeholder="Firm Name"
          confirmLabel={firmModalMode === "edit" ? "Save" : "Create"}
          setCategoryName={(value) => {
            setCategoryName(value);
            if (error) setError(""); // clear error on typing
          }}
          error={error}
          onCancel={handleCloseCategoryModal}
          onCreate={handleSaveFirm}
        />
      )}
      {uploadModal && (
        <UploadLetterheadModal
          isOpen={uploadModal}
          onCancel={() => setUploadModal(false)}
          onUpload={handleUploadLetterhead}
          firmName={categories.find((c) => c.key === selectedCategory)?.label || ""}
          firmId={selectedCategory}
          uploading={uploading}
        />
      )}
      {editModal && templateToEdit && (
        <UploadLetterheadModal
          isOpen={editModal}
          onCancel={() => {
            setEditModal(false);
            setTemplateToEdit(null);
            setTemplateToEditWithFilenames(null);
          }}
          onUpload={handleUpdateLetterhead}
          firmName={categories.find((c) => c.key === selectedCategory)?.label || ""}
          firmId={selectedCategory}
          uploading={uploading}
          initialData={{
            firm_location: templateToEdit.firm_location,
            is_default: templateToEdit.is_default,
          }}
          isEditMode={true}
          templateToEdit={templateToEditWithFilenames ?? templateToEdit}
        />
      )}
      {deleteModal && templateToDelete && (
        <CustomModal
          open={deleteModal}
          onClose={() => {
            setDeleteModal(false);
            setTemplateToDelete(null);
          }}
          heading="Delete Letterhead"
          content={`Are you sure you want to delete "${templateToDelete.name || templateToDelete.file_filename}"? This action cannot be undone.`}
          btnTxtPrimary="Delete"
          btnTxtSecondary="Cancel"
          onPrimaryClick={confirmDeleteLetterhead}
          onSecondaryClick={() => {
            setDeleteModal(false);
            setTemplateToDelete(null);
          }}
          disabled={isLoading}
          variant="delete"
        />
      )}
      {deleteFirmModal && (
        <CustomModal
          open={!!deleteFirmModal}
          onClose={() => setDeleteFirmModal(null)}
          heading="Delete Firm"
          content={`Are you sure you want to delete "${deleteFirmModal.label}"? This action cannot be undone.`}
          btnTxtPrimary="Delete"
          btnTxtSecondary="Cancel"
          onPrimaryClick={() =>
            handleDeleteFirmUtil({
              selectedFirm: deleteFirmModal,
              deleteFirm,
              loadInitialTemplates,
              handleToastMessage,
              setIsLoading: setisLoading,
              setDeleteFirmModal,
            })
          }
          onSecondaryClick={() => setDeleteFirmModal(null)}
          disabled={isLoading}
          variant="delete"
        />
      )}
      {isLoading && <Loader />}
      {showToast && (
        <ErrorToast
          message={toastMessages}
          onClose={(id) =>
            setToastMessages((prev) => {
              const filtered = prev.filter((msg) => msg.id !== id);
              if (filtered.length === 0) setShowToast(false);
              return filtered;
            })
          }
        />
      )}
    </Container>
  );
};

export default LetterHead;
